import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { progressService } from "../../../../../services/progressService";
import ProfileCard from "../Identity/CurrentYou/ProfileCard";
import stars from "../../../../../assets/stars.png";

const JourneyShowcase = ({ userId, subsectionId, onNextStep }) => {
  const [index, setIndex] = useState(0);
  const [identityData, setIdentityData] = useState(null);
  const [needsData, setNeedsData] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);

  const stages = [
    { id: 1, label: "Identify Strengths" },
    { id: 2, label: "Value Mapping" },
    { id: 3, label: "Value & Pitch" },
    { id: 4, label: "1st Meeting With Career Coach" },
    { id: 5, label: "Visibility Mapping" },
  ];

  useEffect(() => {
    async function loadAllData() {
      try {
        const identityRes = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "identity",
          "current_identity_reflection"
        );
        setIdentityData(identityRes?.payload || null);

        const needsRes = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "vision",
          "needs_mapping"
        );
        const validNeeds =
          Array.isArray(needsRes?.selectedCardsData)
            ? needsRes.selectedCardsData
            : Array.isArray(needsRes?.data?.selectedCardsData)
            ? needsRes.data.selectedCardsData
            : [];
        setNeedsData(validNeeds);

        const stakeholderRes = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "interpersonal",
          "interpersonal_list"
        );
        const validStakeholders = Array.isArray(stakeholderRes?.stakeholders)
          ? stakeholderRes.stakeholders.filter((s) => s.name && s.relation)
          : [];
        setStakeholders(validStakeholders);
      } catch (err) {
        console.error("Failed to fetch journey data:", err);
      }
    }

    if (userId) loadAllData();
  }, [userId]);

  useEffect(() => {
    if (index < 2) {
      const timer = setTimeout(() => setIndex((prev) => prev + 1), 3500);
      return () => clearTimeout(timer);
    }
  }, [index]);

  const renderIdentityCard = () => (
    <ProfileCard
      formData={identityData}
      headerLabel="Current State"
      editableName={false}
      showKnownFor={true}
      showCoreValues={true}
    />
  );

  const renderNeedsCard = () => (
    <div className="bg-white rounded-2xl shadow-xl w-[340px] p-6 text-center border border-gray-100">
      <div className="bg-[#042A2B] text-white text-xs font-semibold px-4 py-2 rounded-t-xl w-full mb-4 uppercase tracking-wide">
        Unmet Needs
      </div>
      {needsData.length > 0 ? (
        <ul className="flex flex-col gap-3 items-center">
          {needsData.map((need, i) => (
            <li
              key={i}
              className="border border-gray-200 rounded-xl px-4 py-2 text-sm font-medium text-gray-800 w-full text-center hover:bg-gray-50 transition"
            >
              {need.label}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-400 text-sm">No unmet needs found.</p>
      )}
    </div>
  );

  const renderStakeholdersCard = (withCTA = false) => (
    <div className="bg-white rounded-[22px] shadow-xl w-[394px] h-[520px] flex flex-col overflow-hidden border border-gray-100">
      <div className="bg-[#0D1111] min-h-20 text-white text-xs md:text-[23.33px] font-bold font-gotham tracking-[0.08em] uppercase py-4 flex items-center justify-center relative">
        <span>Stakeholders</span>
        <div className="absolute left-6 top-1/2 -translate-y-1/2 text-teal-300 text-lg">
          <img src={stars} alt="Stars" className="w-8 h-8" />
        </div>
        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-teal-300 text-lg">
          <img src={stars} alt="Stars" className="w-8 h-8" />
        </div>
      </div>

      <div className="stakeholder-list-scroll flex-1 min-h-0 flex flex-col justify-start items-center p-6 gap-4 bg-white overflow-y-auto">
        {stakeholders.length > 0 ? (
          stakeholders.map((s, i) => (
            <div
              key={i}
              className="w-full flex items-center gap-3 border border-gray-200 rounded-[14px] px-4 py-3 bg-[#FCFCFC] hover:bg-gray-50 transition"
            >
              {s.animal ? (
                <img
                  src={s.animal}
                  alt={s.name}
                  className="w-[36px] h-[36px] object-contain"
                />
              ) : (
                <div className="w-[36px] h-[36px] rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-lg font-bold">
                  {s.name?.charAt(0)}
                </div>
              )}
              <div className="flex flex-col text-left">
                <span className="text-[15px] font-semibold text-[#1C1C1C] leading-tight">
                  {s.name}
                </span>
                <span className="text-[11px] uppercase tracking-wide text-[#7A7A7A] font-medium mt-[1px]">
                  {s.relation}
                </span>
              </div>
            </div>
          ))
        ) : (
          <p className="text-[#0D1111] font-gotham font-medium text-sm mt-10">No stakeholders found.</p>
        )}
      </div>

      {withCTA && (
        <div className="py-6 flex justify-center bg-white border-t border-gray-100">
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            onClick={() => onNextStep?.()}
            className="bg-[#54B0AF] hover:bg-teal-600 text-white px-8 py-3 rounded-full font-gotham font-medium text-sm shadow-md transition"
          >
            Next: Visibility Mapping
          </motion.button>
        </div>
      )}
    </div>
  );

  const frames = [
    { id: 1, label: stages[0].label, content: renderIdentityCard() },
    { id: 2, label: stages[1].label, content: renderNeedsCard() },
    { id: 3, label: stages[3].label, content: renderStakeholdersCard(true) },
  ];

  return (
    <div className="w-full h-screen flex items-center justify-center bg-[#F9FAFB] relative overflow-hidden">
      {/* Central horizontal line */}
      <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-[#E5E5E5] -translate-y-1/2" />

      {/* Animated Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={frames[index].id}
          initial={{ opacity: 0, x: 80 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -80 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="z-10 relative flex flex-col items-center justify-center"
        >
          <div className="flex items-center gap-8">
            {/* Card slightly left */}
            <div className="translate-x-[-40px]">{frames[index].content}</div>

            {/* Circle + label on right */}
            <div className="flex flex-col items-center gap-2 translate-x-[20px]">
              <div className="relative flex items-center justify-center">
                <div className="w-[70px] h-[70px] rounded-full border-[4px] border-[#D9D9D9] bg-white flex items-center justify-center shadow-sm">
                  <div className="w-[30px] h-[30px] rounded-full bg-[#D9D9D9]" />
                </div>
              </div>
              <p className="text-[11px] uppercase font-semibold tracking-[0.1em] text-[#1C1C1C] text-center leading-tight w-[120px]">
                {frames[index].label}
              </p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default JourneyShowcase;
